import { useLanguage, type Locale } from '../i18n';
import { Globe, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface LanguageSwitcherProps {
  value?: Locale;
  onChange?: (locale: Locale) => void;
  variant?: 'light' | 'dark';
}

export default function LanguageSwitcher({ value, onChange, variant = 'light' }: LanguageSwitcherProps) {
  const { locale: appLocale, setLocale: setAppLocale, t } = useLanguage();
  const locale = value ?? appLocale;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDark = variant === 'dark';

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectLocale = (next: Locale) => {
    if (value === undefined) {
      setAppLocale(next);
    }
    onChange?.(next);
    setOpen(false);
  };

  const options: { value: Locale; label: string; flag: string }[] = [
    { value: 'en', label: t.language.en, flag: '🇬🇧' },
    { value: 'de', label: t.language.de, flag: '🇩🇪' },
  ];
  const active = options.find((o) => o.value === locale) || options[0];

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${
          isDark
            ? 'bg-white/5 border-white/10 text-white hover:bg-white/10'
            : 'bg-white border-cloud text-ink hover:border-slate'
        }`}
      >
        <Globe size={16} className={isDark ? 'text-cloud' : 'text-slate'} />
        <span className="hidden sm:inline">{active.label}</span>
        <span className="sm:hidden">{active.flag}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isDark ? 'text-cloud' : 'text-slate'} ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className={`absolute right-0 mt-2 w-44 border shadow-lg z-50 py-1 ${
            isDark ? 'bg-charcoal border-white/10 text-white' : 'bg-white border-cloud text-ink'
          }`}
        >
          {options.map((option) => (
            <li key={option.value} role="option" aria-selected={locale === option.value}>
              <button
                type="button"
                onClick={() => selectLocale(option.value)}
                className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                  isDark ? 'hover:bg-white/10' : 'hover:bg-mist'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden="true">{option.flag}</span>
                  {option.label}
                </span>
                {locale === option.value && <Check size={14} className="text-red-500" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
